import React from "react";
import { Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  NumberOutlined,
  QuestionCircleOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import {
  AssessmentRound,
  SignalSummary,
  SkillEvidence,
} from "../../../../Service/application.insight.service";

const RoundCard = ({ round }: { round: AssessmentRound }) => {
  const roundTag =
    round.status === "qualified"
      ? "green"
      : round.status === "unqualified"
        ? "red"
        : "blue";
  return (
    <div className="round-card">
      <div className="round-card-header">
        <Typography.Text strong>{round.roundName}</Typography.Text>
        <Tag color={roundTag}>{round.status}</Tag>
      </div>
      <div className="round-card-meta">
        <div className="meta-item">
          <NumberOutlined className="meta-icon purple" />
          <span className="meta-label">Round:</span>
          <span className="meta-value">{round.roundNumber}</span>
        </div>
        <div className="meta-item">
          <QuestionCircleOutlined className="meta-icon blue" />
          <span className="meta-label">Questions:</span>
          <span className="meta-value">{round.totalQuestions}</span>
        </div>
        <div className="meta-item">
          <CheckCircleOutlined className="meta-icon green" />
          <span className="meta-label">Attempted:</span>
          <span className="meta-value">{round.attemptNumber}</span>
        </div>
        <div className="meta-item">
          <TrophyOutlined className="meta-icon orange" />
          <span className="meta-label">Qualification:</span>
          <span className="meta-value">{round.qualificationPercentage}%</span>
        </div>
      </div>
    </div>
  );
};

const EvidenceCard = ({
  evidence,
  type,
}: {
  evidence: SkillEvidence;
  type: "strength" | "concern";
}) => {
  const cardClass =
    evidence.status === "not_a_match"
      ? "border-card-danger"
      : evidence.status === "partial_match"
        ? "border-card-warning"
        : "border-card-low";
  return (
    <div className={`risk-card ${cardClass}`}>
      <div className="risk-card-header">
        <Typography.Text strong>{evidence.skillName}</Typography.Text>
        {type === "concern" && (
          <Tag color={evidence.status === "not_a_match" ? "error" : "warning"}>
            {evidence.status === "not_a_match" ? "Gap" : "Partial"}
          </Tag>
        )}
      </div>
      <Typography.Text type="secondary" style={{ fontStyle: "italic" }}>
        {evidence.evidence}
      </Typography.Text>
    </div>
  );
};

interface AssessmentBlockProps {
  displayRounds: AssessmentRound[];
  signalSummary: SignalSummary | null;
}

const AssessmentBlock: React.FC<AssessmentBlockProps> = ({ displayRounds, signalSummary }) => {
  if (displayRounds.length === 0) {
    return null;
  }
  const strengths = signalSummary?.topStrengths ?? [];
  const concerns = signalSummary?.topConcerns ?? [];

  return (
    <div className="round-section">
      <div className="aic-header-title">
        <span
          className="material-symbols-outlined"
          data-icon="list"
          style={{ color: "#000000", fontSize: "16px" }}
        >
          list
        </span>
        <Typography.Text strong>Round Summary</Typography.Text>
      </div>
      <div className="round-wrapper">
        {displayRounds.map((r, i) => (
          <RoundCard round={r} key={i} />
        ))}
      </div>
      <div className="aic-skill-match">
        {strengths.length > 0 && (
          <div>
            <div className="aic-header-title">
              <span
                className="material-symbols-outlined"
                data-icon="person_shield"
                style={{ color: "#000000", fontSize: "16px" }}
              >
                person_shield
              </span>
              <Typography.Text strong>Strengths</Typography.Text>
            </div>
            <div className="skill-card-wrapper">
              {strengths.map((s, i) => (
                <EvidenceCard type="strength" evidence={s} key={i} />
              ))}
            </div>
          </div>
        )}
        {concerns.length > 0 && (
          <div>
            <div className="aic-header-title">
              <span
                className="material-symbols-outlined"
                data-icon="motion_sensor_alert"
                style={{ color: "#000000", fontSize: "16px" }}
              >
                motion_sensor_alert
              </span>
              <Typography.Text strong>Areas of Concern</Typography.Text>
            </div>
            <div className="skill-card-wrapper">
              {concerns.map((c, i) => (
                <EvidenceCard key={i} evidence={c} type="concern" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentBlock;
